import _ from "lodash";
import React, { useCallback, useMemo } from 'react';

import { useStoreState } from "easy-peasy";

import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import {useTranslation} from "react-i18next";
import {types} from "../lib/store";
import {types as SimulationTypes} from "./Simulation";

function AcousticGraph() {
  const { t } = useTranslation();
  const { spectra } = useStoreState(state => state.selection.acousticData);
  const hasSoundData = useStoreState(state => state.selection[types.HAS_SOUND_DATA]);

  const fan = useStoreState(state => state.selection.fan);
  const designPoint = useStoreState(state => state.selection.designPoint);
  const simulationType = useStoreState(state => state.selection.customizations.simulationType);

  const preProcessAcousticData = (data, categories, primaryColor, totalColor) => {
    let i = 0;
    const newData = [];

    _.each(categories, (category) => {
      newData.push({
        y: parseFloat(data[category]),
        x: i,
        color: category === "TOTAL" && categories.length > 1 ? totalColor : primaryColor
      });

      i++;
    });

    return newData;
  };

  const getConfig = useCallback((title, categories) => {
    return {
      chart: {
        type: 'column',
        backgroundColor:'none',
      },
      title: {
        text: title
      },
      subtitle: {
        text: t("frontend:fan.acoustic.espectre")
      },
      tooltip: {
        enabled:false
      },
      credits:{
        enabled:false
      },
      xAxis: {
        categories: categories
      },
      yAxis:{
        labels: {
          formatter: function() {
            return [this.value] +' dB(A)';
          }
        },
        title: {
          text: undefined
        }
      },
      legend: {
        itemStyle: {
          color: '#000000',
          fontWeight: 'normal'
        }
      },
      plotOptions: {
        series: {
          states: {
            inactive: {
              opacity: 1
            }
          }
        },
      },
      series: [
        {
          color: window.FwApp.uiPreferences.primaryColor,
          name: t("frontend:fan.acoustic.sound_power"),
          type: 'column',
          dataLabels: {
            overflow: 'none',
            crop: false,
            enabled: true,
            color: '#000',
            style: {
              font: 'bold 12px Verdana, sans-serif',
              textShadow: '0 0 3px white'
            },
            formatter: function() {
              return this.y > 0 ? Highcharts.numberFormat(this.y, -1) : "";
            }
          }
        },
        {
          color: window.FwApp.uiPreferences.secondaryColor,
          name: t("frontend:fan.acoustic.sound_pressure"),
          type: 'column',
          dataLabels: {
            overflow: 'none',
            crop: false,
            enabled: true,
            color: '#000',
            style: {
              font: 'bold 12px Verdana, sans-serif',
              textShadow: '0 0 3px white'
            },
            formatter: function() {
              return this.y > 0 ? Highcharts.numberFormat(this.y, -1) : "";
            }
          }
        }
      ]
    }
  }, [t]);

  //TESTED WITH: 27905-kuvio-150 and 20806-hbx-63-t4-a5-6
  const getTabName = (spectrum) => {
    //default title is a name of graph or blank.
    //depends of admins fills the curve name when they are part of a group of curves
    let name = spectrum.name || '';

    //if is active VSP and the freq. selected is not a default fan freq. is needed
    //add freq. on each graph title and flag VSP to a acoustic graph with simulated freq.
    if(simulationType === SimulationTypes.FREQUENCY && designPoint.frequency !== fan.frequency) {
      //create title with frequency for show in the title graph for each type of graph
      name = _.trim(`${name} ${spectrum.frequency} Hz`);
      //but only if the current spectra is not equal to default fan freq.
      //is needed VPS flag to end of a title graph
      if(spectrum.frequency !== fan.frequency) {
        name += ' VSD';
      }
    }

    return name;
  };

  const charts = useMemo(() => {
    if (!hasSoundData) {
      return [];
    }

    const charts = [];

    _.each(spectra, (spectrum, index) => {
      const title = getTabName(spectrum);
      const config = getConfig(title, spectrum.categories);

      config.series[0].data = preProcessAcousticData(spectrum.power, spectrum.categories, window.FwApp.uiPreferences.primaryColor, '#b52f2f');
      config.series[1].data = preProcessAcousticData(spectrum.pressure, spectrum.categories, window.FwApp.uiPreferences.secondaryColor, '#631a1a');

      charts.push(config);
    });

    return charts;
  }, [getConfig, hasSoundData, spectra]);

  return (
    <>
      {/* TODO: improve key */}
      { charts.map((chartOptions, index) => <HighchartsReact key={index} highcharts={Highcharts} options={chartOptions} />) }
    </>
  );
}

export default AcousticGraph;
